/**
 * Bakes `translate(...)` transforms into the coordinates they move.
 *
 * The engine's SVG renderer does not apply transforms on groups. Our charts
 * lean on them heavily — a donut is arcs drawn around the origin and moved to
 * the centre, a legend is rows moved down one line at a time — so without this
 * every one of those pieces lands on top of the origin and the chart is
 * unreadable.
 *
 * Flattening at this boundary rather than in the chart code keeps the charts
 * written the way SVG is normally written, and covers custom blocks that emit
 * SVG of their own. Only translation is flattened; anything else is left in
 * place and reported, because silently drawing it wrong is worse than saying so.
 */
import { toHtml, type VNode, type VProps } from "@broadpaper/core";

/** Attributes that carry a coordinate, and which axis each one moves along. */
const COORDS: Record<string, Array<[attr: string, axis: 0 | 1]>> = {
  rect: [["x", 0], ["y", 1]],
  circle: [["cx", 0], ["cy", 1]],
  ellipse: [["cx", 0], ["cy", 1]],
  line: [["x1", 0], ["y1", 1], ["x2", 0], ["y2", 1]],
  text: [["x", 0], ["y", 1]],
  tspan: [["x", 0], ["y", 1]],
  image: [["x", 0], ["y", 1]],
  use: [["x", 0], ["y", 1]]
};

const TRANSLATE = /^\s*translate\(\s*(-?[\d.eE+]+)\s*(?:[, ]\s*(-?[\d.eE+]+))?\s*\)\s*$/;

/** Parses `translate(x, y)`. Returns null for anything else, including no transform. */
function translationOf(value: unknown): [number, number] | null | "unsupported" {
  if (typeof value !== "string" || !value.trim()) return null;
  const m = TRANSLATE.exec(value);
  if (!m) return "unsupported";
  return [Number(m[1]), m[2] === undefined ? 0 : Number(m[2])];
}

/** Number of parameters each path command takes, and which of them are coordinates. */
const PATH_COMMANDS: Record<string, { size: number; coords: number[] }> = {
  m: { size: 2, coords: [0, 1] },
  l: { size: 2, coords: [0, 1] },
  t: { size: 2, coords: [0, 1] },
  h: { size: 1, coords: [0] },
  v: { size: 1, coords: [1] },
  c: { size: 6, coords: [0, 1, 0, 1, 0, 1] },
  s: { size: 4, coords: [0, 1, 0, 1] },
  q: { size: 4, coords: [0, 1, 0, 1] },
  // rx ry rotation large-arc sweep x y — only the final pair is a position.
  a: { size: 7, coords: [-1, -1, -1, -1, -1, 0, 1] },
  z: { size: 0, coords: [] }
};

/**
 * Shifts a path's absolute coordinates. Relative commands describe deltas and
 * are already correct, except for a leading `m`, which the spec treats as
 * absolute because there is no current point to be relative to.
 */
export function translatePathData(d: string, dx: number, dy: number): string {
  if (!dx && !dy) return d;
  const tokens = d.match(/[a-zA-Z]|-?(?:\d*\.\d+|\d+)(?:[eE][-+]?\d+)?/g);
  if (!tokens) return d;

  const out: string[] = [];
  let command = "";
  let i = 0;
  let first = true;

  while (i < tokens.length) {
    const token = tokens[i]!;
    if (/[a-zA-Z]/.test(token)) {
      command = token;
      out.push(token);
      i++;
      if (command.toLowerCase() === "z") {
        first = false;
        continue;
      }
    }
    const spec = PATH_COMMANDS[command.toLowerCase()];
    if (!spec || spec.size === 0) {
      // A command we do not know: copy the rest through untouched rather than
      // guess at its parameters and corrupt the path.
      while (i < tokens.length) out.push(tokens[i++]!);
      break;
    }
    // An implicit repeat of `M`/`m` continues as `L`/`l`.
    const absolute = command === command.toUpperCase() || (first && command === "m");
    for (let p = 0; p < spec.size && i < tokens.length; p++) {
      const axis = spec.coords[p]!;
      const value = Number(tokens[i]!);
      out.push(absolute && axis >= 0 ? String(round(value + (axis === 0 ? dx : dy))) : tokens[i]!);
      i++;
    }
    if (first) {
      first = false;
      if (command === "m") command = "l";
      else if (command === "M") command = "L";
    }
  }
  return out.join(" ");
}

function round(n: number): number {
  return Math.round(n * 1000) / 1000;
}

/** Shifts a `points` list, as used by polyline and polygon. */
function translatePoints(points: string, dx: number, dy: number): string {
  const nums = points.match(/-?(?:\d*\.\d+|\d+)(?:[eE][-+]?\d+)?/g);
  if (!nums) return points;
  return nums.map((n, i) => round(Number(n) + (i % 2 === 0 ? dx : dy))).join(" ");
}

function shift(v: VNode, dx: number, dy: number, warn: (message: string) => void): VNode {
  const own = translationOf(v.props.transform);
  let tx = dx;
  let ty = dy;
  const props: VProps = { ...v.props };

  if (own === "unsupported") {
    warn(`SVG transform "${String(v.props.transform)}" is not translate() and was left to the engine, which ignores it`);
  } else if (own) {
    tx += own[0];
    ty += own[1];
    delete props.transform;
  }

  if (tx || ty) {
    for (const [attr, axis] of COORDS[v.tag] ?? []) {
      // An absent coordinate defaults to zero, and zero still has to move.
      const current = props[attr] as unknown;
      const base = current === undefined || current === null || current === "" ? 0 : Number(current);
      if (Number.isFinite(base)) props[attr] = round(base + (axis === 0 ? tx : ty));
    }
    if (v.tag === "path" && typeof props.d === "string") props.d = translatePathData(props.d, tx, ty);
    if ((v.tag === "polyline" || v.tag === "polygon") && typeof props.points === "string") props.points = translatePoints(props.points, tx, ty);
  }

  const children = v.children.map((c) => (typeof c === "string" ? c : shift(c, tx, ty, warn)));
  return { ...v, props, children };
}

/** A label lifted out of an SVG, in the SVG's own user units. */
export interface SvgLabel {
  x: number;
  y: number;
  text: string;
  anchor: "start" | "middle" | "end";
  fontSize: number;
  fontFamily?: string;
  fontWeight?: number;
  fill?: string;
}

function textContent(v: VNode | string): string {
  if (typeof v === "string") return v;
  return v.children.map(textContent).join("");
}

function numeric(value: unknown, fallback: number): number {
  const n = typeof value === "number" ? value : Number.parseFloat(String(value ?? ""));
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Splits an SVG into the shapes the engine can draw and the labels it cannot.
 *
 * The engine renders paths, rects and circles but not `<text>`, so axis labels,
 * legends and the number in the middle of a donut simply vanish. Lifting them
 * out and drawing them as real text is better than rasterising the chart: the
 * words stay selectable, searchable and available to a screen reader.
 *
 * Coordinates are absolute by this point, because transforms are flattened
 * first. Groups survive as `<g>` so that presentation attributes they carry —
 * fill, stroke, opacity — still inherit to their children.
 */
/** Attributes a browser uses to make a chart interactive; meaningless in a PDF. */
const SCREEN_ONLY = /^data-bp-/;

export function splitSvg(v: VNode, warn: (message: string) => void): { markup: string; labels: SvgLabel[] } {
  const flat = shift(v, 0, 0, warn);
  const labels: SvgLabel[] = [];

  const strip = (node: VNode, inherited: { fontFamily?: string; fill?: string }): VNode | null => {
    const own = {
      fontFamily: (node.props["font-family"] as string | undefined) ?? inherited.fontFamily,
      fill: (node.props.fill as string | undefined) ?? inherited.fill
    };
    if (node.tag === "text") {
      const text = textContent(node).trim();
      if (text) {
        const anchor = String(node.props["text-anchor"] ?? "start");
        labels.push({
          x: numeric(node.props.x, 0),
          y: numeric(node.props.y, 0),
          text,
          anchor: anchor === "middle" || anchor === "end" ? anchor : "start",
          fontSize: numeric(node.props["font-size"], 10),
          fontFamily: own.fontFamily,
          fontWeight: node.props["font-weight"] ? numeric(node.props["font-weight"], 400) : undefined,
          fill: own.fill
        });
      }
      return null;
    }
    const children: Array<VNode | string> = [];
    for (const c of node.children) {
      if (typeof c === "string") {
        children.push(c);
        continue;
      }
      const kept = strip(c, own);
      if (kept) children.push(kept);
    }
    // Screen-only attributes are dropped here, so the markup handed to the
    // engine is exactly what it was before charts became interactive. A PDF has
    // nothing to hover.
    const props: VNode["props"] = {};
    for (const [k, value] of Object.entries(node.props)) if (!SCREEN_ONLY.test(k)) props[k] = value;
    return { ...node, props, children };
  };

  const kept = strip(flat, {})!;
  return { markup: kept.children.map((c) => (typeof c === "string" ? c : toHtml(c))).join(""), labels };
}

/* ------------------------------------------------------------------ */
/* Parsing SVG that arrives as markup rather than as a VNode           */
/* ------------------------------------------------------------------ */

const ENTITIES: Record<string, string> = { amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", nbsp: "\u00a0" };

function decodeEntities(s: string): string {
  return s.replace(/&(#x?[0-9a-fA-F]+|[a-zA-Z]+);/g, (whole, body: string) => {
    if (body.startsWith("#x") || body.startsWith("#X")) return String.fromCodePoint(Number.parseInt(body.slice(2), 16));
    if (body.startsWith("#")) return String.fromCodePoint(Number.parseInt(body.slice(1), 10));
    return ENTITIES[body] ?? whole;
  });
}

const TAG = /<\s*(\/)?\s*([a-zA-Z][\w:.-]*)((?:\s+[\w:.-]+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s">]+))*)\s*(\/)?\s*>/g;
const ATTR = /([\w:.-]+)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s">]+))/g;
const SKIP = /<\?[\s\S]*?\?>|<!--[\s\S]*?-->|<!\[CDATA\[[\s\S]*?\]\]>|<![\s\S]*?>/g;

/**
 * Parses a standalone SVG document into a VNode.
 *
 * Logos and other brand assets routinely arrive as an SVG data URI in an
 * `<img>`, which the engine cannot decode — it produces a grey box. Parsing
 * them here puts them through the same treatment as a chart, so their
 * transforms are flattened and their text is drawn.
 *
 * This handles the subset of XML that SVG assets use: elements, attributes,
 * text and entities. It is not a general XML parser and does not try to be.
 */
export function parseSvgMarkup(source: string): VNode | null {
  const text = source.replace(SKIP, "");
  const root: VNode = { tag: "#root", props: {}, children: [] };
  const stack: VNode[] = [root];
  let cursor = 0;

  TAG.lastIndex = 0;
  for (let m = TAG.exec(text); m; m = TAG.exec(text)) {
    const between = text.slice(cursor, m.index);
    if (between.trim()) stack[stack.length - 1]!.children.push(decodeEntities(between));
    cursor = TAG.lastIndex;

    const [, closing, tag, rawAttrs, selfClosing] = m;
    if (closing) {
      // Tolerate a stray close tag rather than unwinding past the root.
      if (stack.length > 1) stack.pop();
      continue;
    }

    const props: VProps = {};
    ATTR.lastIndex = 0;
    for (let a = ATTR.exec(rawAttrs ?? ""); a; a = ATTR.exec(rawAttrs ?? "")) {
      props[a[1]!] = decodeEntities(a[2] ?? a[3] ?? a[4] ?? "");
    }

    const node: VNode = { tag: tag!, props, children: [] };
    stack[stack.length - 1]!.children.push(node);
    if (!selfClosing) stack.push(node);
  }

  const svg = root.children.find((c): c is VNode => typeof c !== "string" && c.tag.toLowerCase() === "svg");
  return svg ?? null;
}

/** Decodes a `data:image/svg+xml` URI, whether percent-encoded or base64. */
export function svgFromDataUri(src: string): VNode | null {
  const match = /^data:image\/svg\+xml(;[^,]*)?,(.*)$/is.exec(src.trim());
  if (!match) return null;
  const isBase64 = (match[1] ?? "").toLowerCase().includes("base64");
  try {
    const body = match[2] ?? "";
    // This runs in a browser as well as in Node, so decode without Buffer.
    const markup = isBase64 ? new TextDecoder().decode(Uint8Array.from(atob(body), (c) => c.charCodeAt(0))) : decodeURIComponent(body);
    return parseSvgMarkup(markup);
  } catch {
    return null;
  }
}
